import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'
import { PERMISOS_POR_ROL } from '@/composables/usePermisos'
import LoginView from '@/views/LoginView.vue'

const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: {
      requiresAuth: false,
      layout: 'blank'
    }
  },
  {
    path: '/auth/gmail-callback',
    name: 'gmail-callback',
    component: () => import('@/views/auth/GmailCallbackView.vue'),
    meta: {
      requiresAuth: false,
      layout: 'blank'
    }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Dashboard',
      permiso: 'dashboard.ver'
    }
  },
  {
    path: '/emision',
    name: 'emision',
    component: () => import('@/views/EmisionView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Emisión de DTE'
    },
    children: [
      {
        path: 'factura',
        name: 'emision-factura',
        component: () => import('@/views/emision/FacturaView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Nueva Factura',
          tipoDte: '01',
          permiso: 'emision.crear'
        }
      },
      {
        path: 'ccf',
        name: 'emision-ccf',
        component: () => import('@/views/emision/CCFView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Nuevo Crédito Fiscal',
          tipoDte: '03',
          permiso: 'emision.credito_fiscal'
        }
      }
    ]
  },
  {
    path: '/historial',
    name: 'historial',
    component: () => import('@/views/HistorialView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Historial de Emisiones',
      permiso: 'historial.ver'
    }
  },
  {
    path: '/reportes',
    name: 'reportes',
    component: () => import('@/views/ReportesView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Reportes',
      permiso: 'reportes.ver'
    }
  },
  {
    path: '/configuracion',
    name: 'configuracion',
    component: () => import('@/views/ConfiguracionView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Configuración'
    },
    children: [
      {
        path: 'emisor',
        name: 'configuracion-emisor',
        component: () => import('@/views/configuracion/EmisorView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Configuración del Emisor',
          roles: ['SuperAdmin', 'EmisorAdmin', 'GerenteSucursal']
        }
      },
      {
        path: 'emisores',
        name: 'configuracion-emisores',
        component: () => import('@/views/configuracion/EmisoresView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Gestión de Emisores',
          permiso: 'emisores.ver'
        }
      },
      {
        path: 'receptores',
        name: 'receptores',
        component: () => import('@/views/configuracion/ReceptoresView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Gestión de Receptores',
          permiso: 'clientes.ver'
        }
      },
      {
        path: 'sucursales',
        name: 'sucursales',
        component: () => import('@/views/configuracion/SucursalesView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Gestión de Sucursales',
          permiso: 'sucursales.ver'
        }
      },
      {
        path: 'usuarios',
        name: 'configuracion-usuarios',
        component: () => import('@/views/configuracion/UsuariosView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Gestión de Usuarios',
          permiso: 'usuarios.ver'
        }
      },
    ]
  },
  {
    path: '/invalidacion/:id',
    name: 'invalidacion',
    component: () => import('@/views/InvalidacionView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Invalidación de DTE',
      permiso: 'emision.anular'
    }
  },
  // =============================================
  // RUTAS NUEVAS - Módulos adicionales
  // =============================================
  {
    path: '/perfil',
    name: 'perfil',
    component: () => import('@/views/PerfilView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Mi Perfil'
    }
  },
  {
    path: '/configuracion/proveedores',
    name: 'proveedores',
    component: () => import('@/views/configuracion/ProveedoresView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Gestión de Proveedores',
      permiso: 'proveedores.ver'
    }
  },
  {
    path: '/configuracion/cajas',
    name: 'cajas',
    component: () => import('@/views/configuracion/CajasView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Gestión de Cajas',
      permiso: 'cajas.ver'
    }
  },
  {
    path: '/configuracion/vendedores',
    name: 'vendedores',
    component: () => import('@/views/configuracion/VendedoresView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Gestión de Vendedores',
      permiso: 'vendedores.ver'
    }
  },
  {
    path: '/configuracion/tributos',
    name: 'tributos',
    component: () => import('@/views/configuracion/TributosView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Gestión de Tributos'
    }
  },
  {
    path: '/compras',
    name: 'compras',
    component: () => import('@/views/compras/ComprasView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Compras Externas',
      permiso: 'compras.ver'
    }
  },
  {
    path: '/compras/nueva',
    name: 'nueva-compra',
    component: () => import('@/views/compras/CompraDetalleView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Nueva Compra',
      permiso: 'compras.crear'
    }
  },
  {
    path: '/compras/:id',
    name: 'detalle-compra',
    component: () => import('@/views/compras/CompraDetalleView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Detalle de Compra',
      permiso: 'compras.ver'
    }
  },
  {
    path: '/inventario',
    name: 'inventario',
    component: () => import('@/views/inventario/InventarioDashboardView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Inventario',
      permiso: 'inventario.ver'
    }
  },
  {
    path: '/inventario/stock',
    name: 'inventario-stock',
    component: () => import('@/views/inventario/StockView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Stock',
      permiso: 'inventario.stock'
    }
  },
  {
    path: '/inventario/kardex',
    name: 'inventario-kardex',
    component: () => import('@/views/inventario/KardexView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Historial de Producto',
      permiso: 'inventario.ver'
    }
  },
  {
    path: '/inventario/movimientos',
    name: 'inventario-movimientos',
    component: () => import('@/views/inventario/MovimientosView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Movimientos',
      permiso: 'inventario.ver'
    }
  },
  {
    path: '/inventario/categorias',
    name: 'categorias',
    component: () => import('@/views/inventario/CategoriasView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Categorias de Productos',
      permiso: 'categorias.ver'
    }
  },
  {
    path: '/inventario/marcas',
    name: 'marcas',
    component: () => import('@/views/inventario/MarcasView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Marcas de Productos',
      permiso: 'marcas.ver'
    }
  },
  {
    path: '/inventario/tipos-gasto',
    name: 'tipos-gasto',
    component: () => import('@/views/inventario/TiposGastoView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Tipos de Gasto',
      permiso: 'tiposGasto.ver'
    }
  },
  {
    path: '/inventario/productos',
    name: 'catalogo-productos',
    component: () => import('@/views/inventario/ProductosView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Catálogo de Productos',
      permiso: 'productos.ver'
    }
  },
  {
    path: '/inventario/servicios',
    name: 'catalogo-servicios',
    component: () => import('@/views/inventario/ServiciosView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Catálogo de Servicios',
      permiso: 'productos.ver'
    }
  },
  {
    path: '/facturas-pendientes',
    name: 'facturas-pendientes',
    component: () => import('@/views/facturacion/FacturasPendientesView.vue'),
    meta: {
      requiresAuth: true,
      title: 'DTEs Pendientes de Envío',
      permiso: 'pendientes.ver'
    }
  },
  {
    path: '/lotes/:id',
    name: 'detalle-lote',
    component: () => import('@/views/lotes/LoteDetalleView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Detalle de Lote',
      permiso: 'lotes.ver'
    }
  },
  {
    path: '/contingencia',
    name: 'contingencia',
    component: () => import('@/views/contingencia/ContingenciaView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Eventos de Contingencia',
      permiso: 'contingencia.ver'
    }
  },
  {
    path: '/contingencia/crear',
    name: 'crear-contingencia',
    component: () => import('@/views/contingencia/CrearEventoView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Reportar Contingencia',
      permiso: 'contingencia.crear'
    }
  },
  {
    path: '/contingencia/:id',
    name: 'detalle-contingencia',
    component: () => import('@/views/contingencia/EventoDetalleView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Detalle de Contingencia',
      permiso: 'contingencia.ver'
    }
  },
  {
    path: '/reportes/ventas',
    name: 'reportes-ventas',
    component: () => import('@/views/reportes/ReportesVentasView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Reporte de Ventas',
      permiso: 'reportes.ver'
    }
  },
  {
    path: '/reportes/ventas-vendedor',
    name: 'reportes-ventas-vendedor',
    component: () => import('@/views/reportes/ReportesVentasVendedorView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Ventas por Vendedor',
      permiso: 'reportes.ver'
    }
  },
  {
    path: '/reportes/ventas-categoria',
    name: 'reportes-ventas-categoria',
    component: () => import('@/views/reportes/ReportesVentasCategoriaView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Ventas por Categoria',
      permiso: 'reportes.ver'
    }
  },
  {
    path: '/reportes/comparativo-sucursales',
    name: 'reportes-comparativo-sucursales',
    component: () => import('@/views/reportes/ReportesComparativoSucursalesView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Comparativo de Sucursales',
      permiso: 'reportes.ver'
    }
  },
  {
    path: '/reportes/gastos-administrativos',
    name: 'reportes-gastos-administrativos',
    component: () => import('@/views/reportes/ReportesGastosAdministrativosView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Gastos Administrativos',
      permiso: 'reportes.ver'
    }
  },
  {
    path: '/reportes/compras',
    name: 'reportes-compras',
    component: () => import('@/views/reportes/ReportesComprasView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Reporte de Compras',
      permiso: 'reportes.ver'
    }
  },
  {
    path: '/reportes/stock',
    name: 'reportes-stock',
    component: () => import('@/views/reportes/ReportesStockView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Reporte de Stock',
      permiso: 'reportes.ver'
    }
  },
  {
    path: '/reportes/movimientos',
    name: 'reportes-movimientos',
    component: () => import('@/views/reportes/ReportesMovimientosView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Reporte de Movimientos',
      permiso: 'reportes.ver'
    }
  },
  {
    path: '/reportes/kardex',
    name: 'reportes-kardex',
    component: () => import('@/views/reportes/ReportesKardexView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Reporte de Kardex',
      permiso: 'reportes.ver'
    }
  },
  {
    path: '/sin-permisos',
    name: 'sin-permisos',
    component: () => import('@/views/SinPermisosView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Acceso Denegado'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: {
      requiresAuth: false
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Si hay una posición guardada (navegación con botón atrás), usarla
    if (savedPosition) {
      return savedPosition
    }
    // Si hay un hash (ancla), navegar a ese elemento
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    // Por defecto, ir al inicio de la página
    return { top: 0, behavior: 'smooth' }
  }
})

/**
 * Verifica si el usuario tiene un permiso específico.
 * Usa PERMISOS_POR_ROL del frontend (no del backend) para determinar permisos.
 * @param {string} permiso - El permiso a verificar
 * @returns {boolean} true si el usuario tiene el permiso
 */
const tienePermiso = (permiso) => {
  const user = store.getters['auth/currentUser']
  if (!user || !user.rol) return false

  // Obtener permisos del rol desde la definición del frontend
  const permisosDelRol = PERMISOS_POR_ROL[user.rol] || []
  return permisosDelRol.includes(permiso)
}

router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const isAuthenticated = store.getters['auth/isAuthenticated']

  if (requiresAuth && !isAuthenticated) {
    next({
      name: 'login',
      query: { redirect: to.fullPath }
    })
  } else if (to.name === 'login' && isAuthenticated) {
    next({ name: 'dashboard' })
  } else if (requiresAuth && isAuthenticated) {
    // No verificar permisos para la página de sin-permisos ni dashboard (evitar loop)
    if (to.name === 'sin-permisos' || to.name === 'dashboard') {
      next()
      return
    }

    // Verificar restricción por roles (si está definida)
    const rolesPermitidos = to.meta.roles
    if (rolesPermitidos && rolesPermitidos.length > 0) {
      const user = store.getters['auth/currentUser']
      if (!user || !rolesPermitidos.includes(user.rol)) {
        next({ name: 'sin-permisos' })
        return
      }
    }

    // Verificar permisos
    const permisoRequerido = to.meta.permiso
    if (permisoRequerido && !tienePermiso(permisoRequerido)) {
      next({ name: 'sin-permisos' })
    } else {
      next()
    }
  } else {
    next()
  }
})

router.afterEach((to) => {
  const title = to.meta.title || 'Sistema DTE'
  document.title = `${title} | Sistema DTE El Salvador`
})

export default router
